import axios from 'axios';

const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }

    const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    return isLocalhost ? 'http://localhost:5000/api' : '/api';
};

const api = axios.create({
    baseURL: getApiBaseUrl()
});

const isAdminUrl = (url = '') => url.startsWith('/admin') || url.includes('/admin/');

// Os dois tipos de sessão seguem o mesmo desenho: access token curto em
// sessionStorage + refresh token rotacionado no servidor.
const SESSIONS = {
    admin: {
        accessKey: 'adminToken',
        refreshKey: 'adminRefreshToken',
        // Refresh do admin fica em sessionStorage de propósito: a sessão continua
        // viva o dia todo com a aba aberta, mas morre ao fechar o navegador.
        refreshStore: () => sessionStorage,
        refreshUrl: '/admin/auth/refresh',
        loginUrl: '/tocadochefe',
        panelPath: '/tocadochefe/painel',
        clear: () => {
            sessionStorage.removeItem('adminToken');
            sessionStorage.removeItem('adminRefreshToken');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminAuth');
        },
    },
    customer: {
        accessKey: 'customerToken',
        refreshKey: 'customerRefreshToken',
        refreshStore: () => localStorage,
        refreshUrl: '/auth/refresh',
        loginUrl: '/login',
        panelPath: '/minha-conta',
        clear: () => {
            sessionStorage.removeItem('customerToken');
            sessionStorage.removeItem('customerUser');
            localStorage.removeItem('customerRefreshToken');
        },
    },
};

api.interceptors.request.use((config) => {
    const token = isAdminUrl(config.url || '')
        ? sessionStorage.getItem('adminToken')
        : (sessionStorage.getItem('customerToken') || sessionStorage.getItem('adminToken'));

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Uma fila por tipo de sessão: requisições que falharem enquanto a renovação
// estiver em andamento esperam o token novo em vez de disparar outra renovação.
const refreshState = {
    admin: { running: false, queue: [] },
    customer: { running: false, queue: [] },
};

const flushQueue = (kind, error, token = null) => {
    refreshState[kind].queue.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(token);
    });
    refreshState[kind].queue = [];
};

// Retorna null quando não há refresh token guardado (nada a tentar); caso
// contrário devolve a promessa da requisição original já repetida.
const refreshAndRetry = async (kind, originalRequest, error) => {
    const cfg = SESSIONS[kind];
    const refreshToken = cfg.refreshStore().getItem(cfg.refreshKey);
    if (!refreshToken) return null;

    if (refreshState[kind].running) {
        return new Promise((resolve, reject) => {
            refreshState[kind].queue.push({ resolve, reject });
        }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
        });
    }

    originalRequest._retry = true;
    refreshState[kind].running = true;

    try {
        const { data } = await axios.post(`${getApiBaseUrl()}${cfg.refreshUrl}`, { refreshToken });
        sessionStorage.setItem(cfg.accessKey, data.token);
        cfg.refreshStore().setItem(cfg.refreshKey, data.refreshToken);
        flushQueue(kind, null, data.token);
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        return api(originalRequest);
    } catch {
        flushQueue(kind, new Error('Sessão expirada'));
        cfg.clear();
        window.location.href = cfg.loginUrl;
        return Promise.reject(error);
    } finally {
        refreshState[kind].running = false;
    }
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config || {};
        const status = error.response?.status;
        const path = window.location.pathname;
        const url = originalRequest.url || '';
        const kind = isAdminUrl(url) ? 'admin' : 'customer';

        // Falha no próprio login/refresh não deve realimentar o ciclo de renovação.
        const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login');

        if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            const retried = await refreshAndRetry(kind, originalRequest, error);
            if (retried) return retried;
        }

        if (status === 401 || status === 403) {
            if (path.startsWith(SESSIONS.admin.panelPath)) {
                SESSIONS.admin.clear();
                window.location.href = SESSIONS.admin.loginUrl;
            } else if (path.startsWith(SESSIONS.customer.panelPath)) {
                SESSIONS.customer.clear();
                window.location.href = SESSIONS.customer.loginUrl;
            }
        }
        return Promise.reject(error);
    }
);

export const clearAdminSession = SESSIONS.admin.clear;

export default api;
